import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { computePeaks, type Peaks } from '../../audio/assets/bufferCache';
import * as audioOps from '../../domain/audioOps';
import { AUDIO_LANES, MAX_CLIP_LOOPS, MAX_FADE_SECONDS, STEPS_PER_BAR, type AudioClip } from '../../domain/schemas';
import { createId } from '../../lib/ids';
import { useObservable } from '../../lib/useObservable';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { useOpRunner } from '../music/useOpRunner';
import { Alert, Button, Panel, Spinner } from '../ui';
import { formatSeconds } from './format';

const numberClass = 'w-24 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm';
const selectClass = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const CANVAS_HEIGHT = 120;

/** Waveform editor + inspector for the selected clip. Edits never modify the stored file. */
export function ClipEditor() {
  const { project } = useProject();
  const { selection } = useServices();
  const selected = useObservable(selection);
  const clip = project.audioClips.find((c) => c.id === selected.clipId);
  if (!clip) {
    return (
      <Panel title="Clip editor">
        <p className="text-sm text-zinc-400">Select a clip on the timeline to edit its trim, fades, gain and processing.</p>
      </Panel>
    );
  }
  return <ClipEditorFor key={clip.id} clip={clip} />;
}

function ClipEditorFor({ clip }: { clip: AudioClip }) {
  const { project } = useProject();
  const { selection } = useServices();
  const { apply, error, clearError } = useOpRunner();
  const asset = project.assets.find((a) => a.id === clip.assetId);
  const set = (patch: audioOps.ClipPatch) => apply((p) => audioOps.updateClip(p, clip.id, patch));
  const num = (v: string): number | undefined => (v.trim() === '' || !Number.isFinite(Number(v)) ? undefined : Number(v));
  const id = (k: string) => `clip-${k}`;
  const playedSec = clip.durationSec / clip.playbackRate;

  return (
    <Panel
      title={`Clip editor · ${clip.name}`}
      actions={
        <>
          <Button
            size="sm"
            onClick={() => {
              const newId = createId('clp');
              if (apply((p) => audioOps.duplicateClip(p, clip.id, newId))) selection.selectClip(newId);
            }}
          >
            Duplicate
          </Button>
          <Button size="sm" variant="ghost" className="text-red-300" onClick={() => apply((p) => audioOps.removeClip(p, clip.id)) && selection.selectClip(null)}>
            Delete clip
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3">
          <Alert title={error} action={<Button size="sm" variant="ghost" onClick={clearError}>Dismiss</Button>} />
        </div>
      )}
      {!asset ? (
        <Alert title="The audio file for this clip is missing." />
      ) : (
        <Waveform clip={clip} assetName={asset.name} sourceSec={asset.audio?.durationSec ?? clip.offsetSec + clip.durationSec} onTrim={(patch) => set(patch)} />
      )}
      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400" data-testid="clip-summary">
        <div>
          <dt className="inline">Source: </dt>
          <dd className="inline text-zinc-200">{asset?.name ?? 'missing'}</dd>
        </div>
        <div>
          <dt className="inline">Region: </dt>
          <dd className="inline text-zinc-200">
            {clip.offsetSec.toFixed(2)}–{(clip.offsetSec + clip.durationSec).toFixed(2)} s
          </dd>
        </div>
        <div>
          <dt className="inline">Plays for: </dt>
          <dd className="inline text-zinc-200">{formatSeconds(audioOps.clipTimelineSeconds(clip))}</dd>
        </div>
        <div>
          <dt className="inline">Gain: </dt>
          <dd className="inline text-zinc-200">{clip.gainDb} dB</dd>
        </div>
        <div>
          <dt className="inline">Fades: </dt>
          <dd className="inline text-zinc-200">
            in {clip.fadeInSec} s · out {clip.fadeOutSec} s
          </dd>
        </div>
      </dl>

      <fieldset className="mt-4 grid grid-cols-2 gap-3 text-xs text-zinc-300 sm:grid-cols-4 lg:grid-cols-6">
        <legend className="mb-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">Placement and trim</legend>
        <Field id={id('name')} label="Name">
          <input id={id('name')} defaultValue={clip.name} maxLength={120} onBlur={(e) => e.target.value !== clip.name && set({ name: e.target.value })} className={`${numberClass} w-full`} />
        </Field>
        <Field id={id('bar')} label="Start bar">
          <input
            id={id('bar')}
            type="number"
            min={1}
            step={0.25}
            value={Math.round((clip.startStep / STEPS_PER_BAR + 1) * 1000) / 1000}
            onChange={(e) => num(e.target.value) !== undefined && apply((p) => audioOps.moveClip(p, clip.id, { startStep: (num(e.target.value)! - 1) * STEPS_PER_BAR }))}
            className={numberClass}
          />
        </Field>
        <Field id={id('lane')} label="Lane">
          <select id={id('lane')} value={clip.lane} onChange={(e) => apply((p) => audioOps.moveClip(p, clip.id, { lane: Number(e.target.value) }))} className={selectClass}>
            {Array.from({ length: AUDIO_LANES }, (_, l) => (
              <option key={l} value={l}>
                Lane {l + 1}
              </option>
            ))}
          </select>
        </Field>
        <Field id={id('offset')} label="Source start (s)">
          <input id={id('offset')} type="number" min={0} step={0.01} value={clip.offsetSec} onChange={(e) => num(e.target.value) !== undefined && set({ offsetSec: num(e.target.value)! })} className={numberClass} />
        </Field>
        <Field id={id('duration')} label="Length (s of source)">
          <input id={id('duration')} type="number" min={0.01} step={0.01} value={clip.durationSec} onChange={(e) => num(e.target.value) !== undefined && set({ durationSec: num(e.target.value)! })} className={numberClass} />
        </Field>
        <Field id={id('loops')} label={`Repeats (1–${MAX_CLIP_LOOPS})`}>
          <input id={id('loops')} type="number" min={1} max={MAX_CLIP_LOOPS} step={1} value={clip.loops} onChange={(e) => num(e.target.value) !== undefined && set({ loops: num(e.target.value)! })} className={numberClass} />
        </Field>
      </fieldset>

      <fieldset className="mt-4 grid grid-cols-2 gap-3 text-xs text-zinc-300 sm:grid-cols-4 lg:grid-cols-6">
        <legend className="mb-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">Level and processing (non-destructive)</legend>
        <Field id={id('gain')} label={`Gain ${clip.gainDb} dB`}>
          <input id={id('gain')} type="range" min={-48} max={12} step={0.5} value={clip.gainDb} onChange={(e) => set({ gainDb: Number(e.target.value) })} />
        </Field>
        <Field id={id('fadein')} label="Fade in (s)">
          <input id={id('fadein')} type="number" min={0} max={Math.min(MAX_FADE_SECONDS, playedSec)} step={0.05} value={clip.fadeInSec} onChange={(e) => num(e.target.value) !== undefined && set({ fadeInSec: num(e.target.value)! })} className={numberClass} />
        </Field>
        <Field id={id('fadeout')} label="Fade out (s)">
          <input id={id('fadeout')} type="number" min={0} max={Math.min(MAX_FADE_SECONDS, playedSec)} step={0.05} value={clip.fadeOutSec} onChange={(e) => num(e.target.value) !== undefined && set({ fadeOutSec: num(e.target.value)! })} className={numberClass} />
        </Field>
        <Field id={id('rate')} label={`Speed ${clip.playbackRate}×`} hint="Pitch changes with speed (no time-stretch).">
          <input id={id('rate')} type="range" min={0.25} max={4} step={0.05} value={clip.playbackRate} onChange={(e) => set({ playbackRate: Number(e.target.value) })} />
        </Field>
        <Field id={id('filter')} label="Filter">
          <select id={id('filter')} value={clip.filter.type} onChange={(e) => set({ filter: { ...clip.filter, type: e.target.value as AudioClip['filter']['type'] } })} className={selectClass}>
            <option value="off">Off</option>
            <option value="lowpass">Low-pass</option>
            <option value="highpass">High-pass</option>
          </select>
        </Field>
        <Field id={id('freq')} label={`Cutoff ${clip.filter.frequencyHz} Hz`}>
          <input
            id={id('freq')}
            type="range"
            min={0}
            max={1}
            step={0.001}
            disabled={clip.filter.type === 'off'}
            // Logarithmic slider: 20 Hz – 20 kHz.
            value={Math.log(clip.filter.frequencyHz / 20) / Math.log(1000)}
            onChange={(e) => set({ filter: { ...clip.filter, frequencyHz: Math.round(20 * 1000 ** Number(e.target.value)) } })}
          />
        </Field>
      </fieldset>
      <div className="mt-3 flex flex-wrap gap-4 text-sm text-zinc-300">
        {(
          [
            ['muted', 'Mute'],
            ['reverse', 'Reverse'],
            ['normalize', 'Normalize (peak −1 dBFS)'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex items-center gap-1.5">
            <input type="checkbox" checked={clip[key]} onChange={(e) => set({ [key]: e.target.checked })} data-testid={`clip-${key}`} />
            {label}
          </label>
        ))}
      </div>
      <p className="mt-3 text-xs text-zinc-400">
        Every setting here is applied while playing and exporting; the original file is never changed. Speed and pitch are linked: 2× is twice as fast and
        an octave higher.
      </p>
    </Panel>
  );
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span className="text-[11px] text-zinc-400">{hint}</span>}
    </div>
  );
}

/**
 * Overview of the whole source file with the clip's region, fades and trim handles.
 * Peaks are computed once per asset at the canvas resolution (≤ 1024 buckets).
 */
function Waveform({ clip, assetName, sourceSec, onTrim }: { clip: AudioClip; assetName: string; sourceSec: number; onTrim: (patch: audioOps.ClipPatch) => void }) {
  const { buffers, audio } = useServices();
  const engine = useObservable(audio.state);
  const { project } = useProject();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [loaded, setLoaded] = useState<{ key: string; peaks?: Peaks; error?: string } | null>(null);
  const [dragEdge, setDragEdge] = useState<'start' | 'end' | null>(null);
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = canvas.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(200, Math.min(1024, Math.round(entry!.contentRect.width)))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const key = `${clip.assetId}:${width}`;
  useEffect(() => {
    let cancelled = false;
    buffers.get(clip.assetId).then(
      (buffer) => !cancelled && setLoaded({ key, peaks: computePeaks(buffer, width) }),
      (e: unknown) => !cancelled && setLoaded({ key, error: `“${assetName}”: ${e instanceof Error ? e.message : 'could not be loaded'}` }),
    );
    return () => {
      cancelled = true;
    };
  }, [buffers, clip.assetId, assetName, width, key]);
  const current = loaded?.key === key ? loaded : null;
  const peaks = current?.peaks ?? null;
  const loadError = current?.error ?? null;

  // Playhead inside the source while the song plays over this clip.
  const stepSec = audioOps.stepSeconds(project.settings.bpm);
  const passSec = clip.durationSec / clip.playbackRate;
  const into = engine.status === 'playing' && engine.step >= 0 ? (engine.step - clip.startStep) * stepSec : -1;
  const playheadSec = into >= 0 && into < passSec * clip.loops ? clip.offsetSec + ((into % passSec) * clip.playbackRate) : null;

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (!el || !ctx) return;
    el.width = width;
    el.height = CANVAS_HEIGHT;
    const x = (sec: number) => (sec / sourceSec) * width;
    ctx.fillStyle = '#09090b';
    ctx.fillRect(0, 0, width, CANVAS_HEIGHT);
    // Clip region
    const r0 = x(clip.offsetSec);
    const r1 = x(clip.offsetSec + clip.durationSec);
    ctx.fillStyle = 'rgba(20,184,166,0.15)';
    ctx.fillRect(r0, 0, r1 - r0, CANVAS_HEIGHT);
    if (peaks) {
      const mid = CANVAS_HEIGHT / 2;
      const n = peaks.min.length;
      for (let i = 0; i < n; i++) {
        const px = (i / n) * width;
        const inRegion = px >= r0 && px <= r1;
        ctx.fillStyle = inRegion ? '#5eead4' : '#52525b';
        const top = mid - peaks.max[i]! * mid * 0.95;
        const bottom = mid - peaks.min[i]! * mid * 0.95;
        ctx.fillRect(px, top, Math.max(1, width / n), Math.max(1, bottom - top));
      }
    }
    // Fades (drawn in source time; with reverse they appear mirrored in playback order)
    const gainScale = Math.min(1, 10 ** (clip.gainDb / 20));
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const fi = clip.fadeInSec * clip.playbackRate;
    const fo = clip.fadeOutSec * clip.playbackRate;
    const top = CANVAS_HEIGHT * (1 - gainScale) + 2;
    ctx.moveTo(r0, CANVAS_HEIGHT - 2);
    ctx.lineTo(x(clip.offsetSec + fi), top);
    ctx.lineTo(x(clip.offsetSec + clip.durationSec - fo), top);
    ctx.lineTo(r1, CANVAS_HEIGHT - 2);
    ctx.stroke();
    // Trim handles
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(r0 - 1, 0, 3, CANVAS_HEIGHT);
    ctx.fillRect(r1 - 2, 0, 3, CANVAS_HEIGHT);
    if (playheadSec !== null) {
      ctx.fillStyle = '#fcd34d';
      ctx.fillRect(x(playheadSec), 0, 2, CANVAS_HEIGHT);
    }
  }, [peaks, width, clip, sourceSec, playheadSec]);

  const secAt = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(sourceSec, ((event.clientX - rect.left) / rect.width) * sourceSec));
  };
  const onPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const toPx = (sec: number) => (sec / sourceSec) * rect.width;
    if (Math.abs(px - toPx(clip.offsetSec)) < 8) setDragEdge('start');
    else if (Math.abs(px - toPx(clip.offsetSec + clip.durationSec)) < 8) setDragEdge('end');
    else return;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!dragEdge) return;
    const sec = secAt(event);
    const end = clip.offsetSec + clip.durationSec;
    if (dragEdge === 'start') onTrim({ offsetSec: Math.min(sec, end - 0.01), durationSec: end - Math.min(sec, end - 0.01) });
    else onTrim({ durationSec: Math.max(0.01, sec - clip.offsetSec) });
    setDragEdge(null);
  };

  return (
    <div>
      {loadError ? (
        <Alert title={loadError} />
      ) : (
        <>
          {!peaks && <Spinner label="Drawing waveform…" />}
          <canvas
            ref={canvas}
            role="img"
            aria-label={`Waveform of ${assetName}. Clip region ${clip.offsetSec.toFixed(2)} to ${(clip.offsetSec + clip.durationSec).toFixed(2)} seconds of ${sourceSec.toFixed(2)}.`}
            data-testid="waveform"
            data-peaks={peaks ? peaks.min.length : 0}
            className={`block h-[120px] w-full rounded-md border border-zinc-800 ${dragEdge ? 'cursor-ew-resize' : 'cursor-default'}`}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={() => setDragEdge(null)}
          />
        </>
      )}
      <p className="mt-1 text-xs text-zinc-400">Drag the white edges to trim (or use the fields below). Grey: unused audio; yellow line: gain and fades.</p>
    </div>
  );
}
